

















































































































import { Component, Prop, Mixins, Inject } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Components
import UserPermissions from "@/components/app/admin/users/userPermissions.vue";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import LoggedUserMixin from "@/mixins/loggedUserMixin";
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import SupportedLocaleCodes from "@/locales";

// Types
import { IUser, IUserBrandGroup } from "@/models/users";
import {
  IUserProviderData,
  IUserProviderMethods
} from "@/models/providers/user";
import { ILanguage } from "@/models/constants";

interface Form {
  active: boolean;
  admin: boolean;
  document_language_code: ILanguage["code"];
  enabled_2fa?: boolean;
  interface_language_code: ILanguage["code"];
  name: string;
  phone: string | null;
  phone_code: string | null;
  phone_country_code: string | null;
  user_brand_group: IUserBrandGroup[];
  whitelisted_ips?: string;
  ip_whitelist: number;
}

const initialForm = (): Form => {
  return {
    active: true,
    admin: false,
    document_language_code: SupportedLocaleCodes.EN,
    interface_language_code: SupportedLocaleCodes.EN,
    ip_whitelist: 0,
    name: "",
    phone: null,
    phone_code: null,
    phone_country_code: null,
    user_brand_group: []
  };
};

@Component({
  components: {
    "two-fa-switch": () =>
      import("@/components/app/global/auth/TwoFaSwitch.vue"),
    "user-fields": () => import("@/components/app/admin/users/userFields.vue"),
    "user-permissions": UserPermissions
  }
})
export default class UserForm extends Mixins(
  RequestPromises,
  HasValidation,
  LoggedUserMixin
) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    userPermissions: UserPermissions;
  };

  // Inject

  @Inject({ from: "userProvider" })
  public readonly $userProvider!: () => IUserProviderData;

  public get $userData() {
    return this.$userProvider();
  }

  @Inject({ from: "userMethods" })
  public readonly $userMethods!: IUserProviderMethods;

  // Props

  @Prop({ required: true })
  public userId!: IUser["id"];

  // Data

  public form: Form = initialForm();
  public initialForm: Form = initialForm();
  public sendingVerificationEmail = false;
  public isProcessing: Record<string, boolean> = {};

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.initiateForm();
  }

  // Methods

  public async initiateForm() {
    this.form = {
      ..._.pick(this.$userData.user, [
        "name",
        "email",
        "admin",
        "phone",
        "active",
        "phone_code",
        "phone_country_code",
        "enabled_2fa",
        "interface_language_code",
        "document_language_code",
        "verified",
        "ip_whitelist",
        "whitelisted_ips"
      ]),
      user_brand_group: _.map(
        _.get(this.$userData, "user.user_brand_group", []),
        group => _.pick(group, ["brand_id", "group_id"])
      )
    } as Form;
    this.initialForm = _.cloneDeep(this.form);
  }

  public async validateBeforeSubmit() {
    if (!this.canEdit) return;

    try {
      this.$set(this.isProcessing, "submit", true);

      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      await this.updateUser();

      this.initialForm = _.cloneDeep(this.form);
      this.$refs.userPermissions.setInitialState();

      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.staff_user_successfully_updated")
      });

      if (this.isLoggedUser) {
        this.$store.dispatch("auth/admin/getUser");
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.$delete(this.isProcessing, "submit");
    }
  }

  public updateUser() {
    return this.$store.dispatch(`data/${DataModules.USERS}/update`, {
      id: this.$userData.user.id,
      data: this.formValues
    });
  }

  public revertToInitialState() {
    this.form = _.cloneDeep(this.initialForm);
    this.$refs.userPermissions.revertToInitialState();
  }

  public resendVerification() {
    if (!this.canResendVerificationEmail) {
      return;
    }
    this.sendingVerificationEmail = true;
    this.$store
      .dispatch(`data/${DataModules.USERS}/resendVerification`, {
        data: {
          user_id: this.userId
        }
      })
      .then(() => {
        this.$buefy.toast.open({
          message: this.$t("_.verification_email_sent") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => (this.sendingVerificationEmail = false));
  }

  public sendPasswordReset() {
    if (!this.canResetPassword) return;
    this.$set(this.isProcessing, "sendPasswordReset", true);
    this.$store
      .dispatch(`data/${DataModules.USERS}/sendResetPasswordEmail`, {
        user_id: this.userId
      })
      .then(() => {
        this.$buefy.toast.open({
          message: this.$t("_.reset_password_email_sent") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.$delete(this.isProcessing, "sendPasswordReset");
      });
  }

  public confirmDisable2FA() {
    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          confirmButtonText: this.$t("_action.disable_2fa"),
          message: this.$t("_sentence.2fa_confirm_disable", {
            object: (this.$t("_.user") as string).toLowerCase()
          })
        },
        events: {
          confirmed: () => this.disable2FA()
        }
      }
    });
  }

  public disable2FA() {
    if (!this.$userCan("disable_user_2fa")) return;
    this.$set(this.isProcessing, "disable2FA", true);
    this.$store
      .dispatch(`data/${DataModules.USERS}/disable2FA`, {
        user_id: this.userId
      })
      .then(async () => {
        await this.$userMethods.reloadData();
        this.$buefy.toast.open({
          message: this.$t("_.2fa_disabled") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.$delete(this.isProcessing, "disable2FA");
      });
  }

  // Computed

  public get isFormChanged(): boolean {
    return !_.isEqual(this.form, this.initialForm);
  }

  public get formValues() {
    const newValues: any = _.omitBy(this.form, (v, k) => {
      return this.initialForm[k] === v;
    });

    if (
      _.isEqual(this.form.user_brand_group, this.initialForm.user_brand_group)
    ) {
      delete newValues.user_brand_group;
    }

    return newValues;
  }

  public get canDisable2fa() {
    return this.$userCan("disable_user_2fa");
  }

  public get canEdit() {
    return this.$userCan("update_user") && this.$store.state.user.admin;
  }

  public get canResendVerificationEmail() {
    return this.$userCan("resend_user_verification_email");
  }

  public get canResetPassword() {
    return this.$userCan("send_staff_password_reset_email");
  }

  public get isLoggedUser() {
    return this.userId === this.loggedUser.id;
  }

  public get userLabel() {
    return (
      _.get(this.form, "name").trim() ||
      (this.$t("_.this_object", {
        object: this.$t("_.user")
      }) as string).toLowerCase()
    );
  }

  public get isApiToken() {
    return this.$userData.user.api_user;
  }

  // Guarding

  public guardFunctionalities = ["get_user"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
